'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Check, Zap, Cpu, Server, HardDrive } from 'lucide-react'
import { useAuth } from '@clerk/nextjs'
import { getPlanMetadata, formatPrice } from '@/lib/plans'
import { resolveBadge } from '@/lib/availability'
import { Button } from '@/components/ui/button'

interface CatalogSelectorProps {
  initialPlans: any[]
  initialGames: any[]
}

export default function CatalogSelector({ initialPlans, initialGames }: CatalogSelectorProps) {
  const { isSignedIn } = useAuth()
  const [selectedGameSlug, setSelectedGameSlug] = useState<string | null>(null)
  const [selectedVariantSlug, setSelectedVariantSlug] = useState<string | null>(null)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)

  const selectedGame = initialGames.find(g => g.slug === selectedGameSlug)
  
  // Game variants that are active
  const activeVariants = selectedGame
    ? selectedGame.variants.filter((v: any) => v.status === 'active')
    : []

  const selectedVariant = activeVariants.find((v: any) => v.slug === selectedVariantSlug)

  const handleGameSelect = (slug: string) => {
    setSelectedGameSlug(slug)
    setSelectedVariantSlug(null)
    setSelectedPlanSlug(null)
  }

  const handleVariantSelect = (slug: string) => {
    setSelectedVariantSlug(slug)
    setSelectedPlanSlug(null)
  }

  const handlePlanSelect = (slug: string) => {
    setSelectedPlanSlug(slug)
  }

  return (
    <div className="space-y-12">
      {/* ── STEP 1: PILIH GAME ─────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-900 text-text-primary font-mono text-body font-bold">
            1
          </span>
          <h3 className="text-h3 font-display font-bold text-text-primary">Pilih Game</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {initialGames.map(game => {
            const isComingSoon = game.status === 'coming_soon'
            const isSelected = game.slug === selectedGameSlug

            return (
              <button
                key={game.slug}
                onClick={() => !isComingSoon && handleGameSelect(game.slug)}
                disabled={isComingSoon}
                className={`text-left p-6 rounded-lg border text-text-primary transition-all duration-base relative overflow-hidden ${
                  isComingSoon
                    ? 'border-border bg-bg-s1/30 opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'border-violet-700 bg-bg-s2 shadow-modal ring-1 ring-violet-500/20'
                    : 'border-border bg-bg-s1 hover:bg-bg-s2 hover:border-border-strong'
                }`}
              >
                {isComingSoon && (
                  <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-bg-s3 text-warning font-sans text-caption font-semibold">
                    🔑 Coming Soon
                  </div>
                )}
                <h4 className="text-h4 font-display font-bold">{game.name}</h4>
                <p className="text-body-sm text-text-muted mt-1">
                  {isComingSoon ? 'Segera hadir di NoctrHost' : `Sewa server ${game.name} premium`}
                </p>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── STEP 2: PILIH VARIANT ──────────────────────────────────────── */}
      {selectedGameSlug && (
        <div className="space-y-4 animate-shimmer">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-900 text-text-primary font-mono text-body font-bold">
              2
            </span>
            <h3 className="text-h3 font-display font-bold text-text-primary">Pilih Varian</h3>
          </div>
          {activeVariants.length === 0 ? (
            <p className="text-body-sm text-text-subtle bg-bg-s1 p-4 rounded-md border border-border">
              Tidak ada varian aktif untuk game ini saat ini.
            </p>
          ) : (
            <div className="flex flex-wrap gap-4">
              {activeVariants.map((variant: any) => {
                const isSelected = variant.slug === selectedVariantSlug
                return (
                  <button
                    key={variant.slug}
                    onClick={() => handleVariantSelect(variant.slug)}
                    className={`px-5 py-3 rounded-md font-sans text-body-sm font-semibold border transition-all duration-base ${
                      isSelected
                        ? 'bg-violet-700 border-violet-700 text-white shadow-raised'
                        : 'bg-bg-s2 border-border text-text-primary hover:bg-bg-s3 hover:border-border-strong'
                    }`}
                  >
                    {variant.label} ({variant.loader} {variant.version})
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 3: PILIH PLAN ─────────────────────────────────────────── */}
      {selectedVariantSlug && selectedGame && (
        <div className="space-y-4 animate-shimmer">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-900 text-text-primary font-mono text-body font-bold">
              3
            </span>
            <h3 className="text-h3 font-display font-bold text-text-primary">Pilih Plan</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {initialPlans.map(plan => {
              const status = resolveBadge(selectedGame, plan.slug)
              const isSelected = plan.slug === selectedPlanSlug
              const meta = getPlanMetadata(plan.slug)
              
              // Formatting specs
              const ram = `${plan.ram_mb / 1024} GB RAM`
              const cpu = `${plan.cpu} vCPU`
              const storage = `${plan.disk_mb / 1024} GB SSD`
              const price = formatPrice(plan.price_monthly)

              let badgeColor = 'text-text-subtle bg-bg-s2'
              if (status.available) {
                badgeColor = 'text-success bg-bg-s3'
              } else if (status.label === 'Coming soon') {
                badgeColor = 'text-warning bg-bg-s3'
              } else {
                badgeColor = 'text-danger bg-bg-s3'
              }

              return (
                <div
                  key={plan.slug}
                  className={`flex flex-col justify-between p-6 rounded-lg border transition-all duration-base relative ${
                    status.orderable
                      ? isSelected
                        ? 'border-violet-700 bg-bg-s2 shadow-modal ring-1 ring-violet-500/20'
                        : 'border-border bg-bg-s1 hover:bg-bg-s2 hover:border-border-strong'
                      : 'border-border/30 bg-bg-s1/20 opacity-60'
                  }`}
                >
                  <div>
                    {/* Availability Badge */}
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-h4 font-display font-bold text-text-primary">
                        {plan.name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full font-sans text-caption font-semibold border border-border ${badgeColor}`}>
                        {status.label}
                      </span>
                    </div>

                    <p className="text-body-sm text-text-muted min-h-[40px] mb-4">
                      {meta.description}
                    </p>

                    {/* Price */}
                    <div className="mb-6">
                      <span className="font-mono text-h3 font-bold text-text-primary">
                        {price}
                      </span>
                      <span className="text-caption text-text-subtle">/bulan</span>
                    </div>

                    {/* Resource Specs */}
                    <div className="space-y-2 rounded-md bg-bg-s2 p-3 mb-6 border border-border">
                      <div className="flex items-center gap-2 text-body-sm">
                        <Cpu className="h-3.5 w-3.5 text-violet-400 shrink-0" aria-hidden="true" />
                        <span className="text-text-muted">{cpu}</span>
                      </div>
                      <div className="flex items-center gap-2 text-body-sm">
                        <Server className="h-3.5 w-3.5 text-violet-400 shrink-0" aria-hidden="true" />
                        <span className="text-text-muted">{ram}</span>
                      </div>
                      <div className="flex items-center gap-2 text-body-sm">
                        <HardDrive className="h-3.5 w-3.5 text-violet-400 shrink-0" aria-hidden="true" />
                        <span className="text-text-muted">{storage}</span>
                      </div>
                    </div>
                  </div>

                  {status.orderable ? (
                    <button
                      onClick={() => handlePlanSelect(plan.slug)}
                      className={`w-full py-2.5 rounded-md font-sans text-body-sm font-semibold transition-all duration-base ${
                        isSelected
                          ? 'bg-violet-700 hover:bg-violet-600 text-white shadow-raised'
                          : 'bg-bg-s3 text-text-primary border border-border hover:bg-bg-s2'
                      }`}
                    >
                      {isSelected ? 'Terpilih' : 'Pilih Plan'}
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full py-2.5 rounded-md font-sans text-body-sm font-semibold bg-bg-s2 border border-border/30 text-text-disabled cursor-not-allowed"
                    >
                      Tidak Tersedia
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── BOTTOM ACTIONS / CTA BAR ────────────────────────────────────── */}
      {selectedGameSlug && selectedVariantSlug && selectedPlanSlug && selectedGame && selectedVariant && (
        <div className="p-6 rounded-lg border border-violet-700/50 bg-bg-s2/80 shadow-modal flex flex-col md:flex-row items-center justify-between gap-6 animate-shimmer">
          <div>
            <h4 className="text-h4 font-display font-bold text-text-primary">
              Konfigurasi Server Siap!
            </h4>
            <p className="text-body-sm text-text-muted mt-1">
              Game: <strong className="text-text-primary">{selectedGame.name}</strong> ({selectedVariant.label}) | Plan:{' '}
              <strong className="text-text-primary">
                {initialPlans.find(p => p.slug === selectedPlanSlug)?.name}
              </strong>
            </p>
          </div>
          <Button variant="primary" size="lg" asChild>
            <Link
              href={
                isSignedIn
                  ? `/dashboard/checkout?plan=${selectedPlanSlug}&game=${selectedGameSlug}&variant=${selectedVariantSlug}`
                  : `/sign-up?plan=${selectedPlanSlug}&game=${selectedGameSlug}&variant=${selectedVariantSlug}`
              }
            >
              <Zap className="h-4 w-4" aria-hidden="true" />
              Order Server Sekarang
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}
